import saveAs from 'file-saver';
import React, { useMemo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import { useChartMetricByWeekId } from 'src/serverProxy/useChartMetricByWeekId';
import { useDorisMetricByWeekId } from 'src/serverProxy/useDorisMetricByWeekId';
import { formatMetric } from 'src/utils/app';
import { calculateDerivedMetricByWeekId } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { ComparisonPeriod, useAppSelector, useWeekRange, WeekRange } from 'src/utils/Hooks/typedHooks';
import { DORIS_API_METRIC_TYPE } from 'src/utils/constants';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';

const transformUnitsSoldData = (rows) => rows?.map((row) => [row.weekId, row.value]) || [];

const transformTrafficData = (records) => records?.map((row) => [row.weekId, row.value]) || [];

// This will be used in keyMetricCard, so we took out the logic into hooks.
export const useConversionV2ChartData = () => {
  const weekRange = useWeekRange();

  const { response: dorisUnitsSoldResponse, isLoading: loadingDorisUnitsSold } = useDorisMetricByWeekId({
    queryKey: 'doris-units-sold',
    metricType: DorisMetricTypeEnum.UnitsSold,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    resultCount: 120
  });

  const { response: totalTrafficDorisResponse, isLoading: loadingTotalTraffic } = useDorisMetricByWeekId({
    queryKey: 'doris-total-traffic',
    metricType: DORIS_API_METRIC_TYPE.TOTAL_TRAFFIC,
    resultCount: 120
  });

  // Transform units sold data
  const dorisUnitsSoldPrimaryData = dorisUnitsSoldResponse?.primary?.records;
  const dorisUnitsSoldComparisonData = dorisUnitsSoldResponse?.comparison?.records;

  const unitsSoldPrimaryData = transformUnitsSoldData(dorisUnitsSoldPrimaryData);
  const unitsSoldComparisonData = transformUnitsSoldData(dorisUnitsSoldComparisonData);

  // Transform traffic data
  const { primary: trafficPrimaryResponse, comparison: trafficComparisonResponse } = totalTrafficDorisResponse;
  const trafficPrimaryData = transformTrafficData(trafficPrimaryResponse?.records);
  const trafficComparisonData = transformTrafficData(trafficComparisonResponse?.records);

  const conversionRatePrimaryData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldPrimaryData,
    dataSet2: trafficPrimaryData
  });
  const conversionRateComparisonData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldComparisonData,
    dataSet2: trafficComparisonData
  });

  const {
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange,
    primaryLastWeekValue,
    comparisonLastWeekValue,
    primaryPercentChangeLastWeek
  } = useChartMetricByWeekId({
    primaryRawData: conversionRatePrimaryData,
    secondaryRawData: conversionRateComparisonData,
    primaryDerivedMetricRawData: {
      dataSource1: unitsSoldPrimaryData,
      dataSource2: trafficPrimaryData
    },
    secondaryDerivedMetricRawData: {
      dataSource1: unitsSoldComparisonData,
      dataSource2: trafficComparisonData
    }
  });

  return {
    loading: loadingDorisUnitsSold || loadingTotalTraffic,
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage: weekRange === WeekRange.LAST_WEEK ? primaryLastWeekValue : primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage:
      weekRange === WeekRange.LAST_WEEK ? comparisonLastWeekValue : comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange:
      weekRange === WeekRange.LAST_WEEK ? primaryPercentChangeLastWeek : primaryDerivedMetricPercentChange
  };
};

/**
 * This component will display a spine chart of the conversion rate by week
 * It will use a combination of elastic search of unitsSold and doris traffic
 *
 */
const AtlasV2ConversionSplineV2Chart = () => {
  const { displayName: mainPeriodDisplayName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplayName, id: comparisonPeriodId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const {
    primarySeries,
    secondarySeries,
    loading,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange
  } = useConversionV2ChartData();

  const exportSplineChartToCsv = useMemo(
    () => () => {
      try {
        const series = [
          {
            name: mainPeriodDisplayName,
            data: primarySeries
          },
          {
            name: comparisonPeriodDisplayName,
            data: secondarySeries
          }
        ];

        const csvContent = convertLineChartSeriesToDelimitedData(
          series,
          comparisonPeriodId === ComparisonPeriod.PRIOR_PERIOD
        );

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `Conversion-rate.csv`);
        return Promise.resolve();
      } catch (e) {
        console.error('Error exporting spline chart to CSV:', e);
        return Promise.reject(e);
      }
    },
    [mainPeriodDisplayName, primarySeries, comparisonPeriodDisplayName, secondarySeries, comparisonPeriodId]
  );

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: 'Conversion Rate',
      primaryLegendProps: {
        displayName: mainPeriodDisplayName,
        metric: formatMetric(primaryDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        change: formatMetric(Math.abs(primaryDerivedMetricPercentChange), METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        direction: getDirection(primaryDerivedMetricPercentChange)
      },
      comparisonLegendProps: {
        displayName: comparisonPeriodDisplayName,
        metric: formatMetric(comparisonDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        })
      },
      loading,
      loadingComponent: <SplineChartLoading />,
      showTitleSummary: true,
      convertSeriesToDelimitedData: () => exportSplineChartToCsv()
    };

    return defaultProps;
  };

  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      <SplineChart
        primaryData={primarySeries}
        secondaryData={secondarySeries}
        metricType={METRICTYPE.PERCENT}
        NoDataPlaceholder={
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        }
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2ConversionSplineV2Chart;
